import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { useAuth } from '@frontegg/react';
import { TreeViewBy } from '../types';
import type { LineageDisplaySettings } from '../types/lineageDisplaySettingsTypes';
import { isInSaaSMode } from '../utils/common';

export type TableDensity = 'compact' | 'regular';

type UserPreferences = {
  treeViewBy?: TreeViewBy;
  visibleColumns?: Record<string, string[]>;
  columnOrder?: Record<string, string[]>;
  density?: Record<string, TableDensity>;
  selectedAiModel?: string;
  lineageDisplaySettings?: Partial<LineageDisplaySettings>;
};

const preferencesStore: Record<string, UserPreferences> = {};
const listeners = new Set<() => void>();

const getPreferences = (storageKey: string): UserPreferences => {
  try {
    if (!preferencesStore[storageKey]) {
      const stored = localStorage.getItem(storageKey);
      preferencesStore[storageKey] = stored ? JSON.parse(stored) : {};
    }
    return preferencesStore[storageKey];
  } catch {
    // Return empty object if localStorage access fails
    return preferencesStore[storageKey] || {};
  }
};

const setPreferences = (storageKey: string, preferences: UserPreferences) => {
  try {
    preferencesStore[storageKey] = preferences;
    localStorage.setItem(storageKey, JSON.stringify(preferences));
    listeners.forEach((listener) => listener());
  } catch {
    // Silently fail if localStorage is not available
  }
};

const useUserPreferences = () => {
  const authHook = isInSaaSMode() ? useAuth : null;
  const authData = authHook?.();

  const storageKey = useMemo(
    () =>
      authData?.user?.id
        ? `user_preferences_${authData?.user?.id}`
        : 'user_preferences',
    [authData],
  );

  const userPreferences = useSyncExternalStore(
    (callback) => {
      listeners.add(callback);
      return () => listeners.delete(callback);
    },
    () => (storageKey ? getPreferences(storageKey) : {}),
  );

  const updateUserPreferences = useCallback(
    <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => {
      if (!storageKey) {
        return;
      }

      const updatedPref = { ...getPreferences(storageKey), [key]: value };
      setPreferences(storageKey, updatedPref);
    },
    [storageKey],
  );

  const removeUserPreferences = useCallback(
    (key: keyof UserPreferences) => {
      if (!storageKey) {
        return;
      }

      const updatedPref = { ...getPreferences(storageKey) };
      try {
        delete updatedPref[key];
      } catch {
        // Silently fail if deletion throws
      }
      setPreferences(storageKey, updatedPref);
    },
    [storageKey],
  );

  return {
    userPreferences,
    updateUserPreferences,
    removeUserPreferences,
  };
};

export default useUserPreferences;
